import React from "react";
import { ImageIcon } from "lucide-react";
import { PresignedImage } from "@/components/common/PresignedImage";
import { cn } from "@/lib/utils";
import type { TopServiceRow } from "./mockData";
import type { ServiceDetailExtras } from "./serviceDetailTypes";

const HERO_IMAGE_CLASS = "h-[144px] w-[224px] shrink-0 rounded-lg object-cover";

interface ServiceDetailHeaderProps {
  service: TopServiceRow;
  extras: ServiceDetailExtras;
}

export const ServiceDetailHeader: React.FC<ServiceDetailHeaderProps> = ({
  service,
  extras,
}) => {
  const [heroError, setHeroError] = React.useState(false);
  const heroUrl = extras.heroImageUrl;
  const heroPhotoId = extras.heroPhotoId;

  React.useEffect(() => {
    setHeroError(false);
  }, [heroPhotoId, heroUrl]);

  let heroMedia = <HeroPlaceholder />;

  if (heroPhotoId && !heroError) {
    heroMedia = (
      <PresignedImage
        fileId={heroPhotoId}
        alt={service.name}
        className={HERO_IMAGE_CLASS}
        onError={() => setHeroError(true)}
      />
    );
  } else if (heroUrl && !heroError) {
    heroMedia = (
      <img
        src={heroUrl}
        alt={service.name}
        className={HERO_IMAGE_CLASS}
        onError={() => setHeroError(true)}
      />
    );
  }

  return (
    <header className="flex flex-col items-center gap-4 text-center">
      <h1 className="w-full text-center font-sans text-xl leading-normal font-medium text-black">
        {service.name}
      </h1>

      {heroMedia}

      <div className="flex flex-col gap-1 text-lg text-[#737373]">
        <p>{extras.operatingHours}</p>
        <p className="font-medium text-[#171717]">{extras.priceLabel}</p>
      </div>
    </header>
  );
};

function HeroPlaceholder() {
  return (
    <div
      className={cn(
        HERO_IMAGE_CLASS,
        "flex items-center justify-center border border-[#E5E5E5] bg-[#FAFAFA] text-[#A3A3A3]",
      )}
    >
      <ImageIcon className="size-8" strokeWidth={1.5} />
    </div>
  );
}
